// Focus navigation between a screen's items, the same for keyboard, pad and (for the focus ring) touch: the CommonUI
// model in decision 0046. A screen is a column of units; a unit is one focusable item, or a row of them (an element
// marked `data-nav-row`: a choice's options, a tab list, a binding's slots). Up and down move between units; left and
// right move inside a row (and select, for radios and tabs) or adjust a slider. Confirm presses the focused item;
// back closes the screen. The planning is pure (testable in Node); the DOM layer below applies it.

/** A navigation intent from any device. */
export type NavIntent = "up" | "down" | "left" | "right" | "confirm" | "back";

/** Where focus is: unit index and item index inside the unit. */
export interface NavPos {
  unit: number;
  item: number;
}

/**
 * Plan a move. `units[i]` is the item count of unit i; `selected[i]` (optional) is the item a unit lands on when
 * entered from above or below (a choice's checked option). Up and down wrap; left and right stop at a row's ends.
 * Returns null when nothing moves.
 */
export function planMove(units: readonly number[], at: NavPos | null, dir: "up" | "down" | "left" | "right", selected: readonly (number | null)[] = []): NavPos | null {
  if (!units.length) return null;
  const enter = (u: number): NavPos => ({ unit: u, item: Math.min(selected[u] ?? 0, Math.max(0, units[u]! - 1)) });
  if (!at) return enter(dir === "up" ? units.length - 1 : 0);
  if (dir === "up" || dir === "down") {
    if (units.length === 1) return null;
    const u = (at.unit + (dir === "down" ? 1 : -1) + units.length) % units.length;
    return enter(u);
  }
  const n = units[at.unit] ?? 0;
  const item = at.item + (dir === "right" ? 1 : -1);
  if (item < 0 || item >= n) return null;
  return { unit: at.unit, item };
}

const FOCUSABLE = 'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])';

/** The focusable, enabled, visible items under `root`, in DOM order. */
export function focusables(root: Element): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => {
    if ((el as HTMLButtonElement).disabled || el.closest("[inert]") || el.getAttribute("aria-hidden") === "true") return false;
    return el.getClientRects().length > 0;
  });
}

/** Group items into units: items sharing a `data-nav-row` ancestor form one unit. */
export function navUnits(items: readonly HTMLElement[]): HTMLElement[][] {
  const units: HTMLElement[][] = [];
  const byRow = new Map<Element, HTMLElement[]>();
  for (const el of items) {
    const row = el.closest("[data-nav-row]");
    if (row) {
      const u = byRow.get(row);
      if (u) u.push(el);
      else {
        const nu = [el];
        byRow.set(row, nu);
        units.push(nu);
      }
    } else units.push([el]);
  }
  return units;
}

const isRange = (el: Element | null): el is HTMLInputElement => el instanceof HTMLInputElement && el.type === "range";
/** A field the player types into, whose left and right keys move the caret. */
const isTextField = (el: Element | null): boolean =>
  el?.tagName === "TEXTAREA" || (el?.tagName === "INPUT" && ["text", "email", "password", "search", "url", "tel", "number"].includes((el as HTMLInputElement).type));
const selectsOnFocus = (el: Element): boolean => ["radio", "tab"].includes(el.getAttribute("role") ?? "");

/** Step a range input by its step and fire `input` and `change`, as a drag would. */
export function stepRange(el: HTMLInputElement, dir: 1 | -1): void {
  const step = Number(el.step) || 1;
  const min = el.min === "" ? 0 : Number(el.min);
  const max = el.max === "" ? 100 : Number(el.max);
  const v = Math.min(max, Math.max(min, Math.round((Number(el.value) + dir * step) / step) * step));
  el.value = String(Number(v.toFixed(6)));
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * Apply a direction to the focus inside `root`. `native` says the key came from a keyboard, whose left and right a
 * focused range input handles itself. Returns the element focused (or adjusted), or null when nothing changed.
 */
export function moveFocus(root: Element, dir: "up" | "down" | "left" | "right", o: { native?: boolean } = {}): HTMLElement | null {
  const units = navUnits(focusables(root));
  const active = root.ownerDocument.activeElement as HTMLElement | null;
  let at: NavPos | null = null;
  units.forEach((u, ui) => {
    const ii = active ? u.indexOf(active) : -1;
    if (ii >= 0) at = { unit: ui, item: ii };
  });
  if ((dir === "left" || dir === "right") && o.native && isTextField(active)) return null; // the caret moves
  // an item that uses the arrow keys itself (a map that pans: `data-nav-keys`) keeps them; Tab and the pad leave it
  if (o.native && active?.hasAttribute("data-nav-keys") && root.contains(active)) return null;
  if ((dir === "left" || dir === "right") && isRange(active)) {
    if (o.native) return null; // the browser steps the slider
    stepRange(active, dir === "right" ? 1 : -1);
    return active;
  }
  const selected = units.map((u) => {
    const i = u.findIndex((el) => el.getAttribute("aria-checked") === "true" || el.getAttribute("aria-selected") === "true");
    return i >= 0 ? i : null;
  });
  const next = planMove(
    units.map((u) => u.length),
    at,
    dir,
    selected,
  );
  if (!next) return null;
  const el = units[next.unit]![next.item]!;
  el.focus();
  if ((dir === "left" || dir === "right") && selectsOnFocus(el)) el.click();
  return el;
}

/** The first item to focus on a screen: `[data-autofocus]`, else the first item. */
export function firstFocus(root: Element): HTMLElement | null {
  return root.querySelector<HTMLElement>("[data-autofocus]") ?? focusables(root)[0] ?? null;
}
